/**
 * Feature Lock Component
 * 
 * Displays a locked feature with upgrade prompt
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Lock, Sparkles } from 'lucide-react';
import { useSubscription } from '../hooks/useSubscription';
import { SubscriptionTier } from '../types/subscription';

interface FeatureLockProps {
  feature: string;
  requiredTier: SubscriptionTier;
  children?: React.ReactNode;
  showUpgradeButton?: boolean;
  message?: string;
}

const LockContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  background: linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%);
  border-radius: 12px;
  border: 2px dashed #d1d5db;
  text-align: center;
`;

const LockIcon = styled(Lock)`
  color: #9ca3af;
  margin-bottom: 1rem;
`;

const Title = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 0.5rem;
`;

const Message = styled.p`
  font-size: 0.95rem;
  color: #6b7280;
  margin: 0 0 1.5rem;
  max-width: 400px;
`;

const UpgradeButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(102, 126, 234, 0.3);
  }
`;

export const FeatureLock: React.FC<FeatureLockProps> = ({
  feature,
  requiredTier,
  children,
  showUpgradeButton = true,
  message,
}) => {
  const { tier } = useSubscription();
  const navigate = useNavigate();

  // If user has access, show children
  const tierOrder: SubscriptionTier[] = ['plus', 'pro', 'elite'];
  const currentTierIndex = tierOrder.indexOf(tier);
  const requiredTierIndex = tierOrder.indexOf(requiredTier);

  if (currentTierIndex >= requiredTierIndex) {
    return <>{children}</>;
  }

  const defaultMessage = message || `This feature requires an upgrade.`;

  const handleUpgrade = () => {
    navigate('/pricing');
  };

  return (
    <LockContainer>
      <LockIcon size={48} />
      <Title>{feature} is Locked</Title>
      <Message>{defaultMessage}</Message>
      {showUpgradeButton && (
        <UpgradeButton onClick={handleUpgrade}>
          <Sparkles size={18} />
          Upgrade
        </UpgradeButton>
      )}
    </LockContainer>
  );
};


